import { writable, type Readable } from 'svelte/store';

/// The PWA is served under /app (see app/vite.config.ts) so the marketing
/// landing can own the site root. Routes stay written app-relative ('/',
/// '/new', '/c/<id>') everywhere; only this module knows about the prefix.
export const BASE = '/app';

/// App-relative route → the URL to put in an href or in history.
export function href(to: string): string {
  return to === '/' ? BASE + '/' : BASE + to;
}

/// A same-origin URL path → the app-relative route. Paths outside BASE are
/// returned as-is, which keeps a stale pre-/app link working in the window
/// between the browser following firebase.json's /c/:id → /app/c/:id redirect
/// and this module reading the new location.
function toRoute(pathname: string): string {
  if (pathname === BASE) return '/';
  if (pathname.startsWith(BASE + '/')) return pathname.slice(BASE.length);
  return pathname || '/';
}

const _path = writable(typeof location !== 'undefined' ? toRoute(location.pathname) : '/');

export const path: Readable<string> = { subscribe: _path.subscribe };

export function navigate(to: string, replace = false): void {
  if (replace) history.replaceState(null, '', href(to));
  else history.pushState(null, '', href(to));
  _path.set(to);
  window.scrollTo(0, 0);
}

/// Intercepts left-clicks on internal <a> tags so they navigate via the SPA
/// router instead of triggering a full page load. External links, modified
/// clicks, downloads, and the server-rendered /share + /to routes fall
/// through to the browser's default behavior.
export function installLinkInterceptor(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('popstate', () => _path.set(toRoute(location.pathname)));
  document.addEventListener('click', (e: MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const anchor = (e.target as HTMLElement | null)?.closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href) return;
    if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;
    if (anchor.origin !== location.origin) return;
    // /share/* and /to are server-rendered by the Cloud Function — let the
    // browser handle them normally so they hit the network, not the SPA.
    if (href.startsWith('/share/') || href === '/to' || href.startsWith('/to?')) return;
    e.preventDefault();
    navigate(toRoute(anchor.pathname) + anchor.search + anchor.hash);
  });
}
